import { Area, AreaConfig } from '@ant-design/plots';
import useSwr from '@hooks/useSwr';
import SessionContext from '@providers/Session/SessionContext';
import {
  Button,
  Col,
  Empty,
  Row,
  Select,
  Spin,
  Tooltip,
  Typography,
} from 'antd';
import React, {
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Filters, FiltersCard } from '@components/Filters';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { IObject } from '@utils/defaultTypes';
import { useGraphColors } from '@hooks/useGraphColors';
import { CreateAlarmButton } from '@components/CreateAlarmButton/CreateAlarmButton';
import { CreateAlarmSteps } from '@components/Insights/CreateAlarm/CreateAlarmSteps';
import RoleChecker from '@components/RoleChecker';
import rolesConstants from '@utils/rolesConstants';
import { TransactionsPayGraphTypes } from '@utils/alarmsConstants';
import * as S from './styles';
import { IOption } from '..';

interface ITags {
  site: string;
  status: string;
}

interface ISeries {
  values: ICharValues;
  tags: ITags;
}

type ICharValues = [timestamp: number, total: number | null][];

interface ICharDataResponse {
  results?: {
    series?: ISeries[];
  }[];
}

type GroupedItem = {
  site: string;
  data: {
    status: string;
    value: ICharValues;
  }[];
};

interface IStyleCss {
  [key: string]: CSSProperties;
}

const MIN_VALUE = 0;
const DEFAULT_FORM_VALUE = {
  groupByTime: '10m',
  lastTime: '3h',
};
const { Paragraph } = Typography;

interface IProps {
  options: {
    lastTimeOptions: IOption[];
    groupedOptions: IOption[];
  };
  setSearchParams: (values: IObject) => void;
  searchParams: IObject;
  getOptionsValue: (searchParam: string, defaultValue: string) => string;
}

const TransactionStatus = ({
  options,
  setSearchParams,
  searchParams,
  getOptionsValue,
}: IProps) => {
  const styles: IStyleCss = {
    formSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      marginBottom: '1rem',
    },
    select: {
      width: '100%',
      maxWidth: '15rem',
    },
    spinner: {
      width: '100%',
      padding: '5rem 1rem',
      margin: 16,
    },
    formTitle: { margin: 0 },
    chartHeaderContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '1rem',
    },
    chartTitle: {
      width: '100%',
      textAlign: 'left',
      fontWeight: '600',
      marginBottom: '0',
      fontSize: '1rem',
    },
    notFoundMessage: {
      margin: '1rem auto 0',
      textAlign: 'center',
    },
  };

  const [formData, setFormData] = useState({
    groupByTime: getOptionsValue(
      searchParams?.groupByTimeStatus,
      DEFAULT_FORM_VALUE?.groupByTime,
    ),
    lastTime: getOptionsValue(
      searchParams?.lastTimeStatus,
      DEFAULT_FORM_VALUE?.lastTime,
    ),
  });
  const { customersSwitch, selectedApp, theme, graphThemeKey } =
    useContext(SessionContext);
  const [data, setData] = useState<GroupedItem[]>([]);
  const [openCreateAlarmModal, setOpenCreateAlarmModal] = useState(false);
  const [selectedAppName, setSelectedAppName] = useState<string>('');
  const [showFilters, setShowFilters] = useState(true);
  const graphColors = useGraphColors();

  const { t, i18n } = useTranslation(['transaction-status']);
  const currentLanguage = i18n.language;

  const fetchParams = useMemo(
    () => ({
      params: {
        produto: selectedApp?.toUpperCase(),
        lastTime: formData?.lastTime,
        groupByTime: formData?.groupByTime,
      },
      headers: {
        customerId: customersSwitch?.selected,
      },
    }),
    [customersSwitch?.selected, selectedApp, formData],
  );

  const { data: chartData, isLoading: charIsLoading } =
    useSwr<ICharDataResponse>(
      '/service-proxy/dashboard/transacao/historico/site_status',
      fetchParams,
    );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const groupedData = chartData?.results?.[0]?.series?.reduce(
          (acc: GroupedItem[], cur) => {
            const hasSite = acc?.find(group => group?.site === cur?.tags?.site);

            if (!hasSite) {
              const siteGroup = {
                site: cur?.tags?.site,
                data: [{ status: cur?.tags?.status, value: cur?.values }],
              };
              acc.push(siteGroup);
            } else {
              const statusGroup = hasSite.data.find(
                statusGroupData => statusGroupData.status === cur?.tags?.status,
              );

              statusGroup
                ? statusGroup.value.push(...cur?.values)
                : hasSite.data.push({
                    status: cur?.tags?.status,
                    value: cur?.values,
                  });
            }

            return acc;
          },
          [],
        );

        if (groupedData?.length) setData(groupedData);
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
      }
    };

    fetchData();
  }, [chartData, fetchParams, formData]);

  const formatData = (groupedItem: GroupedItem) =>
    groupedItem?.data?.flatMap(item =>
      item?.value.map(([timestamp, total]) => ({
        type: item?.status || 'Unknown',
        date: String(timestamp),
        total: total ?? MIN_VALUE,
        count: total ?? MIN_VALUE,
      })),
    ) || [];

  const { isLoadingData, hasData } = useMemo(
    () => ({ isLoadingData: charIsLoading, hasData: !!data?.length }),
    [charIsLoading, data],
  );

  const filters = useMemo(
    () => [
      {
        visible: true,
        col: {
          xs: 24,
          sm: 12,
          md: 8,
          lg: 6,
          xl: 5,
        },
        label: t('transaction-status:labelPeriod'),
        element: (
          <Select
            onChange={lastTime => {
              setFormData(state => ({ ...state, lastTime }));
              setSearchParams({
                lastTimeStatus: lastTime,
              });
            }}
            value={getOptionsValue(
              searchParams?.lastTimeStatus,
              formData?.lastTime,
            )}
            options={options?.lastTimeOptions}
            style={styles.select}
          />
        ),
      },
      {
        visible: true,
        col: {
          xs: 24,
          sm: 12,
          md: 8,
          lg: 6,
          xl: 5,
        },
        label: t('transaction-status:labelGrouping'),
        element: (
          <Select
            onChange={groupByTime => {
              setFormData(state => ({ ...state, groupByTime }));
              setSearchParams({
                groupByTimeStatus: groupByTime,
              });
            }}
            options={options?.groupedOptions}
            value={getOptionsValue(
              searchParams?.groupByTimeStatus,
              formData?.groupByTime,
            )}
            style={styles.select}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData?.groupByTime, formData?.lastTime, options, t, searchParams],
  );

  const charConfig = useMemo(
    () =>
      data?.map?.(value => {
        const colors = graphColors[graphThemeKey];
        return {
          site: value?.site,
          config: {
            data: formatData(value),
            isStack: false,
            areaStyle: {
              fillOpacity: 0.3,
            },
            xField: 'date',
            yField: 'total',
            seriesField: 'type',
            color: colors,
            columnWidthRatio: 0.8,
            legend: {
              position: 'bottom',
              layout: 'horizontal',
              itemName: {
                formatter: (name: string) => name,
                style: {
                  fill: theme?.colors?.text1,
                  fontWeight: 600,
                },
              },
              offsetY: 10,
            },
            tooltip: {
              shared: true,
              formatter: (datum: {
                date: number;
                type: string;
                total: number;
              }) => {
                const date = new Date(datum.date);
                const day = date.getDate().toString().padStart(2, '0');
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const hours = date.getHours().toString().padStart(2, '0');
                const minutes = date.getMinutes().toString().padStart(2, '0');

                const formattedDate =
                  currentLanguage === 'en'
                    ? `${month}/${day} ${hours}:${minutes}`
                    : `${day}/${month} ${hours}:${minutes}`;

                return {
                  name: datum.type,
                  value: datum.total,
                  title: formattedDate,
                };
              },
            },
            meta: {
              date: {
                alias: 'Data',
                type: 'cat',
                formatter: (receivedDate: string) => {
                  const date = new Date(receivedDate);
                  const day = date.getDate().toString().padStart(2, '0');
                  const month = (date.getMonth() + 1)
                    .toString()
                    .padStart(2, '0');

                  return currentLanguage === 'en'
                    ? `${month}/${day}`
                    : `${day}/${month}`;
                },
              },
            },
            xAxis: {
              tickCount: 5,
              label: {
                autoHide: true,
                autoRotate: true,
              },
            },
          },
        };
      }),
    [currentLanguage, data, graphColors, graphThemeKey, theme?.colors?.text1],
  );

  const getLabel = (name: string) => {
    return `${t('transactionsDashboardTitle')}: ${name}`;
  };

  const handleOpenFilters = useCallback(() => {
    setShowFilters(!showFilters);
  }, [showFilters]);

  return (
    <S.DashContainer>
      <Row gutter={[16, 16]}>
        <Col xl={24} xs={24}>
          <S.CardContainer
            title={
              <div className="card-header">
                <div className="card-title-box">
                  <div className="card-title">{t('title')}</div>
                  <Tooltip title={t('transaction-status:tooltipMessage')}>
                    <Button
                      type="link"
                      icon={
                        <QuestionCircleOutlined style={styles.questionIcon} />
                      }
                    />
                  </Tooltip>
                  <Filters isOpen={showFilters} onOpen={handleOpenFilters} />
                </div>
              </div>
            }
          >
            <Row gutter={[24, 24]}>
              <Col xs={24}>
                {filters && (
                  <S.FiltersContainer>
                    <FiltersCard isOpen={showFilters} tableFilters={filters} />
                  </S.FiltersContainer>
                )}
              </Col>
              {(() => {
                if (isLoadingData) {
                  return (
                    <Col xs={24}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '400px',
                        }}
                      >
                        <Spin style={styles.spinner} />
                      </div>
                    </Col>
                  );
                }

                if (hasData) {
                  return (
                    <S.DashboardContainer>
                      <Row gutter={[16, 16]}>
                        {charConfig?.map(value => (
                          <Col md={24} xl={12} key={value.site}>
                            <S.DashboardCardContainer>
                              <div style={styles.chartHeaderContainer}>
                                <Paragraph style={styles.chartTitle}>
                                  {getLabel(value.site)}
                                </Paragraph>
                                <RoleChecker
                                  role={
                                    rolesConstants.INSIGHTS_TRANSACTIONS_ALARM_MANAGE_CONFIGURATION
                                  }
                                >
                                  <CreateAlarmButton
                                    tooltipPosition="leftTop"
                                    openCreateAlarmModal={() => {
                                      setSelectedAppName(value.site);
                                      setOpenCreateAlarmModal(true);
                                    }}
                                  />
                                </RoleChecker>
                              </div>
                              <Area
                                {...(value?.config as unknown as AreaConfig)}
                              />
                            </S.DashboardCardContainer>
                          </Col>
                        ))}
                      </Row>
                    </S.DashboardContainer>
                  );
                }

                return (
                  <Col xs={24}>
                    <Empty
                      description={t('notFoundMessage')}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  </Col>
                );
              })()}
            </Row>
          </S.CardContainer>
        </Col>
      </Row>
      <CreateAlarmSteps
        visible={openCreateAlarmModal}
        onClose={() => setOpenCreateAlarmModal(false)}
        preSelectedValues={{
          tipoGrafico: TransactionsPayGraphTypes.TRANSACTION_STATUS,
          transactionStatus: selectedAppName.toUpperCase(),
        }}
        readOnlyFields={['tipoGrafico', 'transactionStatus']}
      />
    </S.DashContainer>
  );
};

export default TransactionStatus;
